export const getters = {
  defaultSelections: state => (config) => {
    return {
      ...Object.fromEntries(Object.entries(config.options || {}).map(
        ([optionId, option]) => { return [optionId, option.isMulti ? [] : null] }
      )),
      ...config.selected
    }
  },

  preorderOption: () => (config) => {
    const optionAndID = Object.entries(config.options || {}).find(
      ([optionId, option]) => {
        return Object.values(option.selections).filter(selection => selection.sku.startsWith('po_')).length > 0
      }
    )

    if (!optionAndID) return false

    const parts = Object.fromEntries(Object.entries(optionAndID[1].selections).map(
      ([selectionId, selection]) => ([selection.sku, parseInt(selectionId)])
    ))

    return {
      id: optionAndID[0],
      parts
    }
  },

  virtualProduct: () => (config, sku) => {
    return Object.entries(config.options).map(
      ([optionId, option]) => {
        const selection = Object.entries(option.selections).find(
          ([selectionId, selection]) => selection.sku === sku
        )
        if (selection) return { optionId, selectId: selection[0] }
        return false
      }
    ).find(i => i)
  },

  selections: (state, getters, rootState, rootGetters) => (config, designDetails, extraSKUs = [], forcePreorder) => {
    const preorderOption = getters.preorderOption(config)
    const selections = getters.defaultSelections(config)
    const productId = config.bundleId

    Object.entries(designDetails || {}).forEach(
      ([part, details]) => {
        if (!details) return

        const optionId = (details[`bundle${productId}`] || {}).optionId
        const selectId = (details[`bundle${productId}`] || {}).selectId
        selections[optionId] = selectId

        // Handle stock managed SKUs
        const stockManagedSku = rootGetters['product/stockManagedSku'](details.sku)
        if (details.sku !== stockManagedSku) {
          const virtualProduct = getters.virtualProduct(config, stockManagedSku)
          if (virtualProduct) {
            const virtualOptionId = virtualProduct.optionId
            const virtualSelectId = virtualProduct.selectId
            selections[virtualOptionId] = virtualSelectId
          }
        }

        if (rootGetters['product/isOnPreorder'](details.sku) && preorderOption !== false) {
          selections[preorderOption.id] = preorderOption.parts.po_preorder
        }
      }
    )

    if (forcePreorder) {
      selections[preorderOption.id] = preorderOption.parts.po_preorder
    }

    // Add extras defined in extraSKUs
    Object.entries(config.options || {}).forEach(
      ([optionId, option]) => {
        Object.entries(option.selections).forEach(
          ([selectionId, selection]) => {
            if (!extraSKUs.includes(selection.sku)) return
            // Add this part to selections
            selections[optionId] = [...(selections[optionId] || []), selectionId]
          }
        )
      }
    )

    return selections
  },

  price: (state, getters) => (config, designDetails, extraSKUs = []) => {
    config = config || {}
    return Object.entries(getters.selections(config, designDetails, extraSKUs)).map(
      ([optionId, selections]) => {
        if (!selections) return 0
        if (!Array.isArray(selections)) selections = [selections]
        return selections.map(
          selection => (config.options[optionId] || {
            selections: { [selection]: 0 }
          }).selections[selection].price)
      }
    ).flat().reduce((sum, x) => sum + x, 0)
  },

  queryOptions: (state, getters, rootState, rootGetters) => (config, designDetails, extraSKUs = [], sku) => {
    const isOnPreorder = rootGetters['product/isOnPreorder'](sku)
    const selections = getters.selections(config, designDetails, extraSKUs, isOnPreorder)
    const queryOptions = Object.fromEntries(Object.entries(selections).map(([key, val]) => {
      if (Array.isArray(val)) {
        return val.map((selectionId, idx) => {
          return [
            [`bundle_option[${key}][${idx}]`, selectionId],
            [`bundle_option_qty[${key}][${idx}]`, 1]
          ]
        }).flat()
      } else {
        return [
          [`bundle_option[${key}]`, val],
          [`bundle_option_qty[${key}]`, 1]
        ]
      }
    }).flat())

    queryOptions.product = config.bundleId

    // Check to see if we should be auto-applying any coupons, this really should be serverside.
    const promo = rootGetters['promo/activePromoForProduct'](sku) || rootGetters['promo/activePromoWithBanner']
    if (promo && promo.code) queryOptions.coupon = promo.code
    
    return queryOptions
  },

  getQueryFromSku: (state, getters, rootState, rootGetters) => (sku) => {
    // Get product instance from SKU trying the full SKU first before trying
    // just the SKU prefix (as eg sierras-le_mclaren shouldn't resolve to sierras)
    let product = rootGetters['product/productBySkuPrefix'](sku)
    if (!product.sku) product = rootGetters['product/productBySkuPrefix']((sku || '').split('-')[0])

    // Return an empty object if the product can't be resolved, magento will kick out an error
    if (!product.sku) return {}
    
    // Get design details for this product
    let designDetails = {}
    if (product.sku !== sku) {
      // This product has been customised
      designDetails = Object.fromEntries(
        sku.replace(new RegExp(`^${product.sku}-`), '').split('-').map(
          part => part.split('_')
        ).map(
          ([part, choice]) => [part, rootGetters['product/allChoices'][`${part}_${choice}`] || {}]
        )
      )
    }

    return getters.queryOptions(product.bundle_config, designDetails, [], sku)
  }
}